// src/utils/dom.js — DOM utility helpers
import { sendBeacon, PAGE_TITLES } from './constants.js';

/**
 * HTML-escape a string. Null-safe — returns '' for null/undefined.
 * Escapes & < > " ' to prevent XSS.
 */
export function escapeHtml(text) {
    if (text === null || text === undefined) return '';
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Escape a value for a JS string literal inside an HTML attribute.
 * Only used where an inline handler cannot be replaced; prefer addEventListener + dataset.
 */
export function escapeJsAttr(value) {
    if (value === null || value === undefined) return '';
    const js = String(value)
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'")
        .replace(/"/g, '\\"')
        .replace(/\r/g, '\\r')
        .replace(/\n/g, '\\n')
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026');
    return escapeHtml(js);
}

/**
 * Show a single view by ID, hiding all others.
 * Cancels CSS-only fallback animation.
 */
export function showView(viewId) {
    document.body.classList.add('js-resolved');
    ['view-loading', 'view-signin', 'view-pending', 'view-waitlist', 'view-app', 'view-profiles', 'view-create-first-profile', 'view-blocked'].forEach(id => {
        const el = document.getElementById(id);
        if (el) {
            el.classList.remove('active');
            el.classList.add('hidden');
        }
    });
    const target = document.getElementById(viewId);
    if (target) {
        target.classList.remove('hidden');
        target.classList.add('active');
    }
    if (viewId === 'view-signin') sendBeacon('signin_shown');
}

/**
 * Switch the active page panel inside the unified app shell.
 * Updates sidebar active state, page title, and hides/shows content.
 */
export function navigateTo(page) {
    document.querySelectorAll('#view-app .page').forEach(p => p.classList.remove('active'));
    const target = document.getElementById('page-' + page);
    if (target) target.classList.add('active');
    document.querySelectorAll('#appSidebar .pa-nav-item').forEach(item => item.classList.remove('active'));
    const navItem = document.querySelector('#appSidebar .pa-nav-item[data-nav="' + page + '"]');
    if (navItem) navItem.classList.add('active');
    const titleEl = document.getElementById('appPageTitle');
    if (titleEl) titleEl.textContent = PAGE_TITLES[page] || page;
    closeSidebar();
}

/**
 * Toggle mobile sidebar open/closed.
 */
export function toggleSidebar() {
    const sidebar = document.getElementById('appSidebar');
    const overlay = document.getElementById('appSidebarOverlay');
    if (sidebar) sidebar.classList.toggle('open');
    if (overlay) overlay.classList.toggle('open');
}

/**
 * Close mobile sidebar.
 */
export function closeSidebar() {
    const sidebar = document.getElementById('appSidebar');
    const overlay = document.getElementById('appSidebarOverlay');
    if (sidebar) sidebar.classList.remove('open');
    if (overlay) overlay.classList.remove('open');
}

/**
 * Reusable themed confirm dialog — replaces all native confirm() calls.
 * Returns Promise<boolean>.
 */
export function showConfirmDialog(title, message, confirmText, confirmClass) {
    confirmText = confirmText || 'Confirm';
    confirmClass = confirmClass || 'btn-primary';
    return new Promise(function(resolve) {
        var overlay = document.getElementById('confirmModal');
        var titleEl = document.getElementById('confirmModalTitle');
        var bodyEl = document.getElementById('confirmModalBody');
        var btnEl = document.getElementById('confirmModalBtn');
        var cancelEl = document.getElementById('confirmModalCancel');
        var closeEl = document.getElementById('confirmModalClose');
        if (!overlay) { resolve(false); return; }
        titleEl.textContent = title;
        bodyEl.textContent = message;
        btnEl.textContent = confirmText;
        btnEl.className = 'btn ' + confirmClass;
        overlay.classList.add('active');
        var resolved = false;
        function cleanup(result) {
            if (resolved) return;
            resolved = true;
            overlay.classList.remove('active');
            btnEl.removeEventListener('click', onConfirm);
            cancelEl.removeEventListener('click', onCancel);
            closeEl.removeEventListener('click', onCancel);
            overlay.removeEventListener('click', onOverlay);
            document.removeEventListener('keydown', onEscape);
            resolve(result);
        }
        function onConfirm() { cleanup(true); }
        function onCancel() { cleanup(false); }
        function onOverlay(e) { if (e.target === overlay) cleanup(false); }
        function onEscape(e) { if (e.key === 'Escape') { e.preventDefault(); cleanup(false); } }
        btnEl.addEventListener('click', onConfirm);
        cancelEl.addEventListener('click', onCancel);
        closeEl.addEventListener('click', onCancel);
        overlay.addEventListener('click', onOverlay);
        document.addEventListener('keydown', onEscape);
    });
}

/**
 * Show a toast notification — replaces all native alert() calls.
 * type: 'success' | 'error' | 'warning' | 'info'
 * duration: auto-dismiss ms (default 4000, 0 for manual dismiss)
 */
export function showToast(message, type, duration) {
    type = type || 'info';
    duration = duration !== undefined ? duration : 4000;
    var container = document.getElementById('toastContainer');
    if (!container) return;
    var el = document.createElement('div');
    el.className = 'toast-item toast-' + type;
    el.textContent = message;
    container.appendChild(el);
    if (duration > 0) {
        setTimeout(function() { dismissToast(el); }, duration);
    }
}

function dismissToast(el) {
    if (!el || !el.parentNode) return;
    el.classList.add('toast-out');
    setTimeout(function() { if (el.parentNode) el.parentNode.removeChild(el); }, 200);
}

/**
 * Validate that a string looks like a UUID (any version).
 */
export function isValidUuid(value) {
    if (!value || typeof value !== 'string') return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/**
 * Set status text + class on a status element.
 */
export function setStatus(elementId, text, className) {
    const el = document.getElementById(elementId);
    if (!el) return;
    el.textContent = text;
    if (className !== undefined) el.className = className;
}
